import { Button, FlexRow, useTranslate } from '@synqo/frontend-component-library';
import { IconAlertTriangle } from '@tabler/icons-react';
import { type CityOffer } from './cityNotice';
import classes from './LocalityFields.module.css';

// @ai-generated 2026-09-14T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

export interface LocalityOfferBoxProps {
    offer: CityOffer;
    /** The notice's statement — the box only adds what can be done about it. */
    statement: string;
    onAccept: (locality: string) => void;
    /** Keep the city that is in the record and close the box. */
    onKeep: () => void;
}

/**
 * The one place the automation ever offers a city: a button per locality of the postal code.
 *
 * It reads in two ways. With an empty city field it is a plain convenience and looks like one. With
 * a city it contradicts it becomes a warning indicator (not an error): the entry in the record
 * stays until the user decides, and saving remains possible either way — locality data can be
 * incomplete, and it is not the authority on where somebody lives.
 */
export function LocalityOfferBox({ offer, statement, onAccept, onKeep }: LocalityOfferBoxProps) {
    const i18n = useTranslate('');
    const { localities, contested } = offer;
    const isContested = contested !== undefined;

    return (
        <div
            className={isContested ? `${classes.offerBox} ${classes.offerBoxContested}` : classes.offerBox}
            role={isContested ? 'alert' : 'group'}
            aria-label={isContested ? undefined : statement}
        >
            <div className={classes.offerBoxText}>
                {/* The icon carries the warning; the statement is in the text, hence aria-hidden. */}
                {isContested ? (
                    <IconAlertTriangle size={16} stroke={2} className={classes.offerBoxIcon} aria-hidden />
                ) : undefined}
                <span>{isContested ? `${statement} ${i18n('Stattdessen übernehmen')}:` : `${statement}:`}</span>
            </div>
            <FlexRow gap="xs" className={classes.offerBoxActions}>
                {localities.map((locality) => (
                    <Button key={locality} size="xs" onClick={() => onAccept(locality)}>
                        {locality}
                    </Button>
                ))}
                {isContested ? (
                    <Button size="xs" variant="normal" appearance="outlined" onClick={onKeep}>
                        {`${contested} ${i18n('beibehalten')}`}
                    </Button>
                ) : undefined}
            </FlexRow>
        </div>
    );
}
